import { useEffect } from "react";
import { useFieldArray, useFormContext, useWatch } from "react-hook-form";

import PropTypes from "prop-types";

import DeleteIcon from "@mui/icons-material/Delete";
import { FormHelperText, Tooltip } from "@mui/material";
import { DataGrid, GridActionsCellItem } from "@mui/x-data-grid";

import { formatNumber } from "@/features/shared";
import { ProductVariantNameCell, useSnackbar } from "@/features/ui";

export const ProductVariantsSection = ({ editMode = false }) => {
  const {
    control,
    setValue,
    formState: { errors },
  } = useFormContext();
  const { showSnackbar } = useSnackbar();

  const { update, remove } = useFieldArray({
    control,
    name: "purchaseInvoiceProductVariants",
  });

  const productVariants = useWatch({
    control,
    name: "purchaseInvoiceProductVariants",
  });

  useEffect(() => {
    const subtotal = productVariants.reduce((sum, variant) => {
      return sum + (variant.quantity * variant.price - variant.discount);
    }, 0);

    setValue("subtotal", subtotal);
  }, [productVariants, setValue]);

  const handleDeleteClick = (id) => () => {
    const index = productVariants.findIndex((variant) => variant.id === id);

    if (index !== -1) {
      remove(index);
      showSnackbar("Produk berhasil dihapus", "success");
    }
  };

  const columns = [
    {
      field: "name",
      headerName: "Produk Varian",
      flex: 5,
      renderCell: ({ row: { name, sku } }) => (
        <ProductVariantNameCell name={name} sku={sku} />
      ),
    },
    {
      field: "quantity",
      headerName: "Qty",
      type: "number",
      valueFormatter: (value) => formatNumber(value),
      flex: 1,
      editable: true,
    },
    { field: "unitOfMeasurement", headerName: "Unit", flex: 1 },
    ...(editMode
      ? [
          {
            field: "returnedQuantity",
            headerName: "Retur",
            type: "number",
            valueFormatter: (value) => formatNumber(value),
            flex: 1,
          },
        ]
      : []),
    {
      field: "price",
      headerName: "@Harga",
      type: "number",
      valueFormatter: (value) => formatNumber(value),
      flex: 1,
      editable: true,
    },
    {
      field: "discount",
      headerName: "Diskon",
      type: "number",
      flex: 1,
      editable: true,
      valueFormatter: (value) => formatNumber(value || 0),
    },
    {
      field: "totalPrice",
      headerName: "Total Harga",
      type: "number",
      flex: 1.5,
      valueGetter: (_value, row) => {
        const { quantity, price, discount } = row;
        return quantity * price - (discount || 0);
      },
      valueFormatter: (value) => formatNumber(value),
    },
    {
      field: "actions",
      type: "actions",
      headerName: "Aksi",
      getActions: ({ id, row }) => {
        const hasReturns = editMode && row.returnedQuantity > 0;

        if (hasReturns) {
          return [
            <Tooltip
              key={id}
              title="Tidak dapat dihapus karena sudah ada retur"
            >
              <span>
                <GridActionsCellItem
                  icon={<DeleteIcon />}
                  label="Hapus"
                  disabled
                />
              </span>
            </Tooltip>,
          ];
        }

        return [
          <GridActionsCellItem
            key={id}
            icon={<DeleteIcon />}
            label="Hapus"
            className="textPrimary"
            onClick={handleDeleteClick(id)}
            color="error"
          />,
        ];
      },
    },
  ];

  const isCellEditable = (params) => {
    if (!editMode) return true;
    return !(params.row.returnedQuantity > 0);
  };

  const processRowUpdate = async (newRow) => {
    return new Promise((resolve, reject) => {
      // Clearing a numeric cell commits null (MUI's numeric valueParser), which
      // slips past the guards below and reaches the payload. Coerce to 0 first.
      const row = {
        ...newRow,
        quantity: Number(newRow.quantity) || 0,
        price: Number(newRow.price) || 0,
        discount: Number(newRow.discount) || 0,
      };
      const { quantity, price, discount, returnedQuantity } = row;
      const totalCost = quantity * price;

      if (quantity < 1) {
        reject(new Error("Kuantitas tidak boleh kurang dari 1"));
        return;
      }

      if (editMode && returnedQuantity > 0 && quantity < returnedQuantity) {
        reject(
          new Error(
            `Kuantitas tidak boleh kurang dari ${returnedQuantity} (sudah diretur)`
          )
        );
        return;
      }

      if (price < 0) {
        reject(new Error("Harga tidak boleh kurang dari 0"));
        return;
      }

      if (discount < 0) {
        reject(new Error("Diskon tidak boleh kurang dari 0"));
        return;
      }

      if (discount > totalCost) {
        reject(new Error("Diskon tidak boleh lebih besar dari Total Harga"));
        return;
      }

      const index = productVariants.findIndex(
        (variant) => variant.id === row.id
      );

      if (index !== -1) {
        update(index, row);
        resolve(row);
      } else {
        reject(new Error("Produk tidak ditemukan. Muat ulang halaman lalu coba lagi."));
      }
    });
  };

  return (
    <>
      {errors.purchaseInvoiceProductVariants && (
        <FormHelperText error>
          ERROR: {errors.purchaseInvoiceProductVariants.message}
        </FormHelperText>
      )}
      <DataGrid
        getRowHeight={() => "auto"}
        editMode="row"
        columns={columns}
        rows={productVariants}
        isCellEditable={isCellEditable}
        processRowUpdate={processRowUpdate}
        onProcessRowUpdateError={(error) => {
          showSnackbar(error.message, "error");
        }}
        initialState={{
          sorting: {
            sortModel: [{ field: "name", sort: "asc" }],
          },
        }}
      />
    </>
  );
};

ProductVariantsSection.propTypes = {
  editMode: PropTypes.bool,
};
